import {
    CheckCircleOutlined,
    CreditCardOutlined,
    FormOutlined,
    GlobalOutlined,
    PieChartOutlined,
    SafetyOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { Col, Row } from 'antd';
import React from 'react';
import { useParams } from 'react-router-dom';

import LeiLoginlessFooter from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessFooter';
import LeiLoginlessMenu from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessMenu';
import LeiZadostForm from 'components/molecules/Lei/zadostLeiLoginless/LeiZadostForm';

import { ETypZadosti } from 'enums/lei/leiTypZadosti';

import GLEIF from 'styles/images/GLEIF.png';

interface IIdentifikaceSpolecnosti {}

const openPdf = (type: 'jurisdikce' | 'cert') => {
    const pdfJurisdikce = 'https://www.cdcp.cz/wp-content/uploads/CDCP_Akreditovane_jurisdikce_LEI.pdf';
    const pdfCert = 'https://www.cdcp.cz/wp-content/uploads/CSDP_Certificate.gif';

    return window.open(type === 'jurisdikce' ? pdfJurisdikce : pdfCert, '_blank', 'noopener,noreferrer');
};

const IdentifikaceSpolecnosti: React.FC<IIdentifikaceSpolecnosti> = () => {
    const { typ } = useParams<{ typ: ETypZadosti }>();

    return (
        <>
            <LeiLoginlessMenu />

            <section className="lei-landing-hero">
                <div className="lei-landing-hero-inner">
                    <div className="lei-landing-hero-copy">
                        <h1>
                            LEI kód online
                            <br />
                            <span className="lei-landing-subtitle">během několika minut</span>
                        </h1>
                        <p
                            style={{
                                marginTop: 30,
                                marginBottom: 50,
                                fontSize: 20,
                                maxWidth: 475
                            }}
                        >
                            Rychlé vydání a správa LEI kódu pro právnické osoby a finanční instituce
                        </p>
                        <ul className="lei-landing-highlights">
                            <li>Akreditovaný vydavatel LEI (GLEIF)</li>
                            <li>Největší poskytovatel LEI v České republice</li>
                            <li>Odborná podpora v češtině</li>
                        </ul>
                    </div>
                </div>
                <div className="lei-stats-bar">
                    <div className="lei-stat">
                        <TeamOutlined className="lei-stat-icon" />
                        <div>
                            <strong>45 000+</strong>
                            <span>vydaných LEI</span>
                        </div>
                    </div>
                    <div className="lei-stat">
                        <PieChartOutlined className="lei-stat-icon" />
                        <div>
                            <strong>90 %</strong>
                            <span>tržního podílu v ČR</span>
                        </div>
                    </div>
                    <a
                        className="lei-stat"
                        onClick={() => openPdf('jurisdikce')}
                        style={{
                            cursor: 'pointer'
                        }}
                    >
                        <GlobalOutlined className="lei-stat-icon" />
                        <div>
                            <strong>20+</strong>
                            <span>zemí</span>
                        </div>
                    </a>
                    <a
                        className="lei-stat"
                        onClick={() => openPdf('cert')}
                        style={{
                            cursor: 'pointer'
                        }}
                    >
                        <img src={GLEIF} alt="gleif" className="lei-stat-icon" height={34} />
                        <div>
                            <strong>Accredited</strong>
                            <span>LOU</span>
                        </div>
                    </a>
                </div>
            </section>
            <div className="lei-landing-page">
                <Row
                    className="lei-landing-layout"
                    style={{
                        minHeight: 0,
                        maxWidth: 1272,
                        margin: '0 auto',
                        padding: '14px 36px 8px'
                    }}
                    justify="center"
                >
                    <Col
                        xs={24}
                        lg={16}
                        className="lei-application-card"
                        style={{
                            position: 'relative',
                            top: 0,
                            height: 'fit-content',
                            padding: '12px 20px 18px',
                            zIndex: 1,
                            backgroundColor: '#fff',
                            border: '1px solid #e1e5e8',
                            borderRadius: 8
                        }}
                    >
                        <div className="lei-form-and-trust">
                            <LeiZadostForm typ={typ} />
                            <aside className="lei-trust-panel">
                                <SafetyOutlined className="lei-trust-icon" />
                                <div>
                                    <strong>Bezpečné a důvěryhodné</strong>
                                    <p>Jsme akreditovaný vydavatel LEI pod globální organizací GLEIF.</p>
                                    <a href="https://www.gleif.org/" target="_blank" rel="noopener noreferrer">
                                        Zjistit více o LEI →
                                    </a>
                                </div>
                            </aside>
                        </div>
                    </Col>
                </Row>
                <div className="lei-landing-info-grid">
                    <section className="lei-info-card">
                        <h2>Jak to funguje</h2>
                        <div className="lei-process-steps">
                            <div className="lei-process-step">
                                <span className="lei-step-number">1</span>
                                <div className="lei-process-title">
                                    <FormOutlined />
                                    <strong>Vyplnění formuláře</strong>
                                </div>
                                <p>Zadejte základní údaje o vaší organizaci.</p>
                            </div>
                            <div className="lei-process-step">
                                <span className="lei-step-number">2</span>
                                <div className="lei-process-title">
                                    <SafetyOutlined />
                                    <strong>Ověření údajů</strong>
                                </div>
                                <p>Ověříme vaše údaje a oprávněnost.</p>
                            </div>
                            <div className="lei-process-step">
                                <span className="lei-step-number">3</span>
                                <div className="lei-process-title">
                                    <CreditCardOutlined />
                                    <strong>Platba online</strong>
                                </div>
                                <p>Proveďte platbu bezpečně online.</p>
                            </div>
                            <div className="lei-process-step">
                                <span className="lei-step-number">4</span>
                                <div className="lei-process-title">
                                    <CheckCircleOutlined />
                                    <strong>Přidělení LEI</strong>
                                </div>
                                <p>
                                    LEI kód vám zašleme{' '}
                                    <span
                                        style={{
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        e-mailem
                                    </span>
                                    .
                                </p>
                            </div>
                        </div>
                    </section>
                    <section className="lei-info-card lei-why-card">
                        <div>
                            <h2>Proč získat LEI u CDCP</h2>
                            <ul>
                                <li>Oficiální lokální vydavatel LEI</li>
                                <li>Podpora v českém jazyce</li>
                                <li>Automatické připomenutí obnovy</li>
                                <li>Bezpečné online zpracování</li>
                                <li>Akreditace GLEIF</li>
                            </ul>
                        </div>
                        <a
                            className="lei-certificate-link"
                            href={
                                window.location.href.includes('test')
                                    ? 'https://test.cdcp.cz/wp-content/uploads/CSDP_Certificate.gif'
                                    : 'https://www.cdcp.cz/wp-content/uploads/CSDP_Certificate.gif'
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={
                                    window.location.href.includes('test')
                                        ? 'https://test.cdcp.cz/wp-content/uploads/gleif-accredited-logo.png'
                                        : 'https://www.cdcp.cz/wp-content/uploads/gleif-accredited-logo.png'
                                }
                                alt="GLEIF Accredited LOU"
                            />
                        </a>
                    </section>
                </div>
            </div>
            <LeiLoginlessFooter />
        </>
    );
};

export default IdentifikaceSpolecnosti;
